/**
 * XEP-0085 chat states — the typing indicator. A bodyless message carrying one
 * state element, stamped with its sender's `<author/>` on the way through:
 *
 * ```
    <message from="10623@acme/web" to="9250@acme" type="chat">
      <composing xmlns="http://jabber.org/protocol/chatstates"/>
      <author xmlns="attensi:chat:0" handle="10623"/>
    </message>
 * ```
 *
 * In a group it arrives inside the MUC/Sub envelope like any group message.
 * Only ever live: history and catch-up never hold one.
 */

import { xml } from "@xmpp/client"
import type { Element as XmlEl } from "@xmpp/xml"
import type { ChatState, ChatStateNotice } from "../../../types"
import { NS_ATTENSI_CHAT, NS_CHATSTATES } from "../../constants"
import { isRoomJid, toJid, type Jid } from "../../jid"
import { attr } from "../../xmlUtils"
import { unwrap } from "../message/unwrap"

const CHAT_STATES: readonly string[] = [
  "active",
  "composing",
  "paused",
  "inactive",
  "gone",
] satisfies ChatState[]

const isChatState = (name: string): name is ChatState =>
  CHAT_STATES.includes(name)

/** The state element a bodyless message carries, if any. */
const stateElement = (message: XmlEl): XmlEl | undefined =>
  message.getChild("body")
    ? undefined
    : message
        .getChildElements()
        .find(
          (child) =>
            child.getNS() === NS_CHATSTATES && isChatState(child.getName())
        )

/**
 * Whether a bounce is one of our chat states coming back. The server echoes the
 * refused payload, and nothing the user did failed: the next send says why.
 */
export const isBouncedChatState = (stanza: XmlEl): boolean =>
  stateElement(stanza) !== undefined

/**
 * The chat state a stanza carries, or null when it carries none or is a real
 * message. A body wins: a message with a state attached is still a message.
 */
export function parseChatState(stanza: XmlEl): ChatStateNotice | null {
  const message = unwrap(stanza)

  const element = stateElement(message)
  if (!element) return null

  const from = attr(message, "from")
  const to = attr(message, "to")
  if (!from || !to) return null

  return {
    state: element.getName() as ChatState,
    author: attr(message.getChild("author", NS_ATTENSI_CHAT), "handle"),
    routing: { from: toJid(from), to: toJid(to) },
  }
}

/** `type` follows the peer, as for a message, or a room refuses it. */
export function buildChatState(peer: Jid, state: ChatState) {
  return xml(
    "message",
    { to: peer, type: isRoomJid(peer) ? "groupchat" : "chat" },
    xml(state, { xmlns: NS_CHATSTATES })
  )
}
